import { VelocityError } from '@/api/audit/velocity-feed-audit';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Column, ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, EyeOff } from 'lucide-react';

const SortIcon = ({ column }: { column: Column<VelocityError> }) => {
	const sorted = column.getIsSorted();
	if (sorted === 'asc') return <ArrowUp className="ml-1 h-4 w-4 text-blue-500" />;
	if (sorted === 'desc') return <ArrowDown className="ml-1 h-4 w-4 text-blue-500" />;
	return <ArrowUpDown className="ml-1 h-4 w-4 opacity-40" />;
};

export const getColumns = (
	handleSingleHide: ((id: number) => void) | undefined,
	isActiveTab: boolean,
): ColumnDef<VelocityError>[] => {

	return [
		{
			id: 'select',
			header: ({ table }) => (
				<div className="flex justify-center pl-4">
					<Checkbox
						checked={table.getIsAllPageRowsSelected()}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label="Select all"
					/>
				</div>
			),
			cell: ({ row }) => (
				<Checkbox
					className="mx-auto"
					checked={row.getIsSelected()}
					onCheckedChange={(value) => row.toggleSelected(!!value)}
					aria-label="Select row"
				/>
			),
			enableSorting: false,
			enableHiding: false,
		},
		{
			accessorKey: 'id',
			header: ({ column }) => (
				<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
					Id <SortIcon column={column} />
				</Button>
			),
			cell: ({ row }) => <div>{row.original.id}</div>,
		},
		{
			accessorKey: 'category',
			header: ({ column }) => (
				<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
					Category <SortIcon column={column} />
				</Button>
			),
			cell: ({ row }) => <div>{row.original.category}</div>,
		},
		{
			accessorKey: 'level1Org',
			header: () => <div className="ml-4">Level1Org</div>,
			cell: ({ row }) => <div>{row.original.level1Org}</div>,
		},
		{
			accessorKey: 'pipsId',
			header: () => <div className="ml-4">PIPS ID</div>,
			cell: ({ row }) => <div>{row.original.pipsId}</div>,
		},
		{
			accessorKey: 'name',
			header: () => <div className="ml-4">Name</div>,
			cell: ({ row }) => <div>{row.original.name}</div>,
		},
		{
			accessorKey: 'error',
			header: ({ column }) => (
				<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
					Error <SortIcon column={column} />
				</Button>
			),
			cell: ({ row }) => <div>{row.original.error}</div>,
		},
		{
			accessorKey: 'errorDate',
			header: ({ column }) => (
				<Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
					Error Date <SortIcon column={column} />
				</Button>
			),
			cell: ({ row }) => {
				const date = row.original.errorDate
					? format(new Date(row.original.errorDate), 'P')
					: '---';

				return <div className="flex items-center justify-between">{date}</div>;
			},
		},
		...(isActiveTab
			? [
					{
						id: 'hide',
						header: () => <div className="ml-2 whitespace-nowrap">Hide Row</div>,
						cell: ({ row }) => {
							return (
								<div className="ml-2 flex items-center justify-between">
									{handleSingleHide && row.original.id != null && (
										<Button
											variant="ghost"
											size="icon"
											onClick={() => handleSingleHide(row.original.id!)}
											className="group relative"
											title="Hide this row"
										>
											<span className="relative inline-block h-4 w-4">
												<Eye
													size={16}
													className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0"
												/>
												<EyeOff
													size={16}
													className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
												/>
											</span>
										</Button>
									)}
								</div>
							);
						},
					} as ColumnDef<VelocityError>,
				]
			: []),
	];
};
